import { Fragment, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./StatusBadge";
import { DrillDialog } from "./DrillDialog";
import { fmtKpi, toneCls, weekLabel } from "./kpiFormat";
import { BY_SERVICE_KPIS, ExpandToggle, GridServiceRows, byService } from "./ServiceBreakdown";

const Cell = ({ r, unit, testId }) => {
  if (!r) return <span className="text-zinc-300">—</span>;
  if (r.value === null || r.value === undefined) {
    return <span data-testid={testId} title={r.reason || "Not available"} className="text-zinc-400 italic">N/A</span>;
  }
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1", toneCls(r.status))}
      title={r.provisional ? "Provisional — recommended rule in use until signed off" : undefined}>
      {fmtKpi(r.value, unit)}
      {r.provisional && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-label="provisional" />}
    </span>
  );
};

const HeaderCell = ({ w, selected, onSelect }) => (
  <th className={cn("px-3 py-2 text-left align-bottom min-w-[118px] border-l border-zinc-800 cursor-pointer transition-colors",
    selected ? "bg-zinc-800" : "hover:bg-zinc-800/70")}
    onClick={() => onSelect(w.week_start)} data-testid={`kpi-grid-week-${w.week_start}`}>
    <div className="font-mono-num text-xs text-white">{weekLabel(w)}</div>
    <div className="mt-1"><StatusBadge status={w.status} testId={`kpi-grid-status-${w.week_start}`} /></div>
  </th>
);

const productSales = (w) => {
  const r = w.results?.rolling_30d_revenue;
  if (!r) return { empty: true };
  const c = r.components || {};
  if (c.product_sales === undefined) return { missing: true };
  return { value: c.product_sales, cats: c.product_sales_by_category || [] };
};

const ProductSalesRow = ({ weeks, selected, onSelect }) => (
  <tr className="bg-zinc-50/60" data-testid="kpi-grid-product-sales-row">
    <td className="sticky left-0 z-10 bg-zinc-50 px-4 py-1.5 pl-8 text-xs text-zinc-500">↳ of which product sales</td>
    {weeks.map((w) => {
      const p = productSales(w);
      return (
        <td key={w.week_start} onClick={() => onSelect(w.week_start)}
          className={cn("px-3 py-1.5 font-mono-num text-xs text-zinc-500 border-l border-zinc-100 cursor-pointer",
            selected === w.week_start && "bg-blue-50/60")}>
          <span data-testid={`kpi-cell-product-sales-${w.week_start}`}
            title={p.missing ? "Not in this snapshot — shown from the next draft onward"
              : p.cats?.map((c) => `${c.name}: ${fmtKpi(c.net, "money2")}`).join("\n") || undefined}>
            {p.empty ? <span className="text-zinc-300">—</span> : p.missing ? "—" : fmtKpi(p.value, "money2")}
          </span>
        </td>
      );
    })}
  </tr>
);

export const WeeklyGrid = ({ grid, selected, onSelect }) => {
  const [open, setOpen] = useState(() => new Set());
  const [drill, setDrill] = useState(null);
  const closeDrill = useCallback(() => setDrill(null), []);
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
  <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white" data-testid="kpi-weekly-grid">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-zinc-950 text-white">
          <th className="sticky left-0 z-10 bg-zinc-950 px-4 py-2 text-left text-xs font-semibold uppercase tracking-[0.2em] min-w-[250px]">
            Living Energy
          </th>
          {grid.weeks.map((w) => <HeaderCell key={w.week_start} w={w} selected={selected === w.week_start} onSelect={onSelect} />)}
        </tr>
      </thead>
      <tbody>
        {grid.rows.map((row) => row.section ? (
          <tr key={row.row} className="bg-zinc-900 text-white">
            <td className="sticky left-0 z-10 bg-zinc-900 px-4 py-1.5 text-2xs font-semibold uppercase tracking-[0.18em]">{row.section}</td>
            {grid.weeks.map((w) => <td key={w.week_start} className="border-l border-zinc-800" />)}
          </tr>
        ) : (
          <Fragment key={row.id}>
          <tr className="border-t border-zinc-100 hover:bg-zinc-50/70">
            <td className="sticky left-0 z-10 bg-white px-4 py-2 text-zinc-700">
              {BY_SERVICE_KPIS.has(row.id) && grid.weeks.some((w) => byService(w.results?.[row.id]))
                ? <ExpandToggle open={open.has(row.id)} onToggle={() => toggle(row.id)} testId={`kpi-grid-expand-${row.id}`} />
                : BY_SERVICE_KPIS.has(row.id) && <span className="mr-1 inline-block w-5" />}
              {row.label}
            </td>
            {grid.weeks.map((w) => (
              <td key={w.week_start} onClick={() => onSelect(w.week_start)}
                className={cn("px-3 py-2 font-mono-num border-l border-zinc-100 cursor-pointer",
                  selected === w.week_start && "bg-blue-50/60")}>
                <Cell r={w.results?.[row.id]} unit={row.unit} testId={`kpi-cell-${row.id}-${w.week_start}`} />
              </td>
            ))}
          </tr>
          {row.id === "rolling_30d_revenue" && <ProductSalesRow weeks={grid.weeks} selected={selected} onSelect={onSelect} />}
          {open.has(row.id) && <GridServiceRows row={row} weeks={grid.weeks} selected={selected} onSelect={onSelect}
            onDrill={(week, r, service) => setDrill({ week, kpi: { id: r.id, label: r.label, unit: r.unit, service } })} />}
          </Fragment>
        ))}
      </tbody>
    </table>
    {drill && <DrillDialog week={drill.week} kpi={drill.kpi} onClose={closeDrill} />}
  </div>
  );
};
